import {
    ApplicationInsights,
    ICustomProperties,
    Snippet,
} from '@microsoft/applicationinsights-web';
import {
    IKEY_DEVELOPMENT,
    IKEY_PRODUCTION,
    PRODUCTION_HOST,
    CLOUD_ROLE_NAME_KEY,
    ENV_KEY,
    FRONTEND_KEY,
    TENANT_NAME_KEY,
    PRODUCTION_KEY,
    URL_PATH,
    USER_ROLE,
} from '../constants';
import { Tenant } from '../tenant';

declare global {
    // eslint-disable-next-line no-unused-vars
    interface Window {
        appInsights: typeof AppInsights;
    }
}

/**
 * Boolean flag
 */
const isProduction = window.location.host === PRODUCTION_HOST;

/**
 * Environment
 */
const host = window.location.host.split('.');

/**
 * Environment name
 */
const env = host.length === 2 ? PRODUCTION_KEY : host[0];

/**
 * Sets the instrumentationKey based on the environment
 */
const iKey = isProduction ? IKEY_PRODUCTION : IKEY_DEVELOPMENT;

/**
 * Application Insights configuration object
 */
const snippet: Snippet = {
    config: {
        instrumentationKey: iKey,
        disableAjaxTracking: false,
        enableCorsCorrelation: true,
        maxAjaxCallsPerView: -1,
        enableUnhandledPromiseRejectionTracking: true,
        enableAjaxErrorStatusText: true,
    },
};

/**
 * AppInsights instance
 */
const AppInsights = new ApplicationInsights(snippet);

/**
 * Adding AppInsight instance to globalThis
 */
window.appInsights = AppInsights;

/**
 * Initialize the instance
 */
AppInsights.loadAppInsights();

/**
 * Add telemetry initializer
 * @param tenant
 */
export function addTelemetryInitializer(
    tenant: Tenant['name'] | null,
    userRole?: string
) {
    if (!tenant) {
        throw new Error(
            'Can not initializer telemetry with empty property args'
        );
    }

    AppInsights.addTelemetryInitializer((envelope) => {
        const telemetryItem = envelope.baseData;
        telemetryItem.properties = telemetryItem.properties || {};
        telemetryItem.properties[CLOUD_ROLE_NAME_KEY] = FRONTEND_KEY;
        telemetryItem.properties[TENANT_NAME_KEY] = tenant;
        telemetryItem.properties[ENV_KEY] = env;
        telemetryItem.properties[URL_PATH] = window.location.pathname;
        telemetryItem.properties[USER_ROLE] = userRole;
    });
}

/**
 * Track event appInsights
 * @param {string} name
 * @param {ICustomProperties} properties
 */
export const trackEvent = (name: string, properties: ICustomProperties) =>
    AppInsights.trackEvent({ name }, properties);

/**
 * Track page view appInsights
 * @param {string} name
 */
export const trackPageView = (name: string) =>
    AppInsights.trackPageView({ name });

/**
 * Log an exception that you have caught.
 * @param exception
 */
export const trackException = (exception: Error) =>
    AppInsights.trackException({ exception });
/**
 * Application Insights instance
 */
export default AppInsights;
